import { Box, Button, Collapse, IconButton, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../Context";
import { useTranslation } from "react-i18next";
import LoadingWheel from "../../LoadingWheel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styled from "@emotion/styled";
import { initialScores } from "../../../Pages/Stats/__fixtures__/initialStates";
import moment from "moment";
import StatsCard from "./StatsCard/StatsCard";
import PlayerImage from "../../PlayerImage/PlayerImage";
import DownloadModal from "../../DownloadModal/DownloadModal";
import { green, text, white } from "../../../../theme/colours";

const PlayerCard = ({ isOpen = true }) => {
  const { t } = useTranslation(["Profile", "Stats"]);
  const { userState, firebaseState } = useContext(GlobalContext);

  const [expandCard, setExpandCard] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [userScores, setUserScores] = useState(initialScores);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const currentDate = moment();

  const userDateRange = {
    min: currentDate.format("YYYY-WW"),
    max: currentDate.format("YYYY-WW"),
  };

  const ExpandMore = styled((props) => {
    const { ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    height: "fit-content",
    transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  const convertScores = (scores) => ({
    acceleration: scores.acceleration,
    focus: scores.alert,
    handling: scores.cool,
    cornering: scores.cornering,
    braking: scores.deceleration,
    eco: scores.eco,
    steadiness: scores.jerk,
    phone: scores.phoneHandling,
    speed: scores.speeding,
    swerve: scores.swerve,
    total: scores.total,
  });

  const handleExpandCard = () => {
    setExpandCard(!expandCard);
  };

  const cardTransition = (style) => {
    return `${style} 0.25s ease`;
  };

  const openDownloadModal = () => {
    setDownloadModalOpen(true);
  };

  const loadingStyle = { width: "fit-content", m: "0 0.625rem 10px" };

  const animatedBox = {
    transition: cardTransition("max-height") + ", " + cardTransition("padding"),
    maxHeight: "fit-content",
    p: isOpen ? "1rem" : "0 0.5rem 10px",
  };

  const playerCardStyle = {
    transition: cardTransition("width") + ", " + cardTransition("background"),
    width: isOpen ? "100%" : "67px",
    borderRadius: "5px",
    background: `linear-gradient(${expandCard ? 155 : 112}deg, ${white} 0%, ${
      green[3]
    } 100%)`,
  };

  const userStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    m: "0 !important",
    p: 0,
    textTransform: "none",
    background: "transparent !important",
    justifyContent: "flex-start",
  };

  const textAreaStyle = {
    transition: cardTransition("margin-left"),
    ml: !isOpen && "0.75rem",
    whiteSpace: "nowrap",
    color: text.primary,
  };

  const levelStyle = { fontSize: "1rem", fontWeight: 500 };

  const expandMoreStyle = {
    ml: "auto !important",
    background: "transparent !important",
    "& svg": { fill: text.primary },
  };

  const statsCardStyle = { m: "0 1rem 0.75rem", borderRadius: "5px" };

  const downloadButtonStyle = {
    width: "100%",
    color: text.primary,
    padding: "0.75rem",
    background: "transparent !important",
    m: "0 !important",
    "& p": { fontSize: "1rem", fontWeight: 600 },
  };

  const imageStyles = {
    transition: cardTransition("margin") + ", " + cardTransition("width"),
    m: isOpen ? "0.75rem" : "0.375rem",
  };

  useEffect(() => {
    const getUserScores = async () => {
      const allScores = await firebaseState.getUserScoresInRange(
        userDateRange.min,
        userDateRange.max,
        userState.profileID,
      );
      setUserScores(convertScores(allScores));
    };
    try {
      setStatsLoading(true);
      userState.profileID && getUserScores();
    } catch (e) {
      console.error(e.message);
    }
    setStatsLoading(false);
  }, [
    firebaseState,
    userState.profileID,
    userDateRange.min,
    userDateRange.max,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setExpandCard(false);
    }
  }, [isOpen]);

  return !userState.profileID ? (
    <Box sx={loadingStyle}>
      <LoadingWheel width={4.125} />
    </Box>
  ) : (
    <Box sx={animatedBox}>
      <Box sx={playerCardStyle}>
        <Button sx={userStyle} onClick={isOpen && handleExpandCard}>
          <PlayerImage
            styles={imageStyles}
            type={userState?.avatar}
            width={isOpen ? "50px" : "35px"}
          />
          <Box sx={textAreaStyle}>
            <Typography variant="h2">{userState?.fn}</Typography>
            <Typography variant="body2" sx={levelStyle}>
              {t("levelNumber", { level: userState?.level })}
            </Typography>
          </Box>
          <ExpandMore expand={expandCard} sx={expandMoreStyle}>
            <ExpandMoreIcon />
          </ExpandMore>
        </Button>
        <Collapse in={expandCard && isOpen} timeout="auto" unmountOnExit>
          <StatsCard
            style={statsCardStyle}
            scores={userScores}
            isLoading={statsLoading}
          />
          <Button onClick={openDownloadModal} sx={downloadButtonStyle}>
            <Typography>{t("downloadStats")}</Typography>
          </Button>
        </Collapse>
      </Box>
      <DownloadModal
        stats={userScores}
        open={downloadModalOpen}
        setOpen={setDownloadModalOpen}
      />
    </Box>
  );
};

export default PlayerCard;
